<div class="workflow-restrictions-config">
  <h5 class="mb-4 text-primary">
    <i class="fa-shield-halved fas me-2"></i>
    Service Validation Rules
  </h5>

  <div class="card p-4 mb-4">
    <form [formGroup]="restrictionsForm" *ngIf="restrictionsForm">
      <!-- Enable/Disable Toggle -->
      <div class="form-group mb-4">
        <div class="d-flex justify-content-between align-items-start">
          <div class="flex-grow-1 me-3">
            <label class="form-label d-block">
              <strong>Block New Applications by State</strong>
              <i
                class="fa-solid fa-circle-info text-muted ms-2"
                placement="top"
                ngbTooltip="When enabled, controls whether users can create new applications based on their existing application states"></i>
            </label>
            <small class="text-muted d-block mb-3">
              Enable this option to prevent users from creating new applications
              when they have existing applications in certain states.
            </small>
          </div>

          <div
            class="form-check form-switch d-flex align-items-center flex-shrink-0 flex-nowrap">
            <input
              class="form-check-input"
              type="checkbox"
              role="switch"
              id="blockStateEnabled"
              formControlName="enabled"
              style="width: 3rem; height: 1.5rem" />
            <label class="form-check-label ms-2" for="blockStateEnabled">
              <span
                class="badge"
                [class.bg-success]="isRestrictionsEnabled"
                [class.bg-warning]="!isRestrictionsEnabled"
                [class.text-dark]="!isRestrictionsEnabled">
                {{ isRestrictionsEnabled ? 'Enabled' : 'Disabled' }}
              </span>
            </label>
          </div>
        </div>
      </div>

      <!-- Mode Selection (only shown when enabled) -->
      <div class="form-group mb-4" *ngIf="isRestrictionsEnabled">
        <label class="form-label d-block mb-3">
          <strong>Blocking Mode</strong>
          <i
            class="fa-solid fa-circle-info text-muted ms-2"
            placement="top"
            ngbTooltip="Choose how the states list should be applied"></i>
        </label>

        <div class="mode-selection">
          <!-- Block Listed Mode -->
          <div
            class="form-check mb-3 p-3 border rounded"
            [class.border-primary]="currentMode === BlockStateMode.BLOCK_LISTED"
            [class.bg-light]="currentMode === BlockStateMode.BLOCK_LISTED"
            style="cursor: pointer"
            (click)="onModeChange(BlockStateMode.BLOCK_LISTED)">
            <input
              class="form-check-input"
              type="radio"
              name="blockMode"
              id="blockListed"
              [value]="BlockStateMode.BLOCK_LISTED"
              formControlName="mode"
              style="cursor: pointer" />
            <label
              class="form-check-label ms-2"
              for="blockListed"
              style="cursor: pointer">
              <strong
                >Block Listed
                <span class="text-danger"
                  >(Prevent if user has ANY of these states)</span
                ></strong
              >
              <br />
              <small class="text-muted">
                Users cannot apply if they have applications in ANY of the
                specified states. <br />
                <strong>Example:</strong> Block if user has PENDING_RESUBMISSION
                or SUBMITTED.
              </small>
            </label>
          </div>

          <!-- Allow Listed Mode -->
          <div
            class="form-check mb-3 p-3 border rounded"
            [class.border-primary]="currentMode === BlockStateMode.ALLOW_LISTED"
            [class.bg-light]="currentMode === BlockStateMode.ALLOW_LISTED"
            style="cursor: pointer"
            (click)="onModeChange(BlockStateMode.ALLOW_LISTED)">
            <input
              class="form-check-input"
              type="radio"
              name="blockMode"
              id="allowListed"
              [value]="BlockStateMode.ALLOW_LISTED"
              formControlName="mode"
              style="cursor: pointer" />
            <label
              class="form-check-label ms-2"
              for="allowListed"
              style="cursor: pointer">
              <strong
                >Allow Listed
                <span class="text-success"
                  >(Allow ONLY if user has ONLY these states)</span
                ></strong
              >
              <br />
              <small class="text-muted">
                Users can apply ONLY if ALL their applications are in the
                specified states. <br />
                <strong>Example:</strong> Allow only if all applications are
                COMPLETED or REJECTED.
              </small>
            </label>
          </div>
        </div>
      </div>

      <!-- States Configuration (only shown when enabled) -->
      <div class="form-group" [class.disabled-section]="!isRestrictionsEnabled">
        <label class="form-label d-block mb-3">
          <strong>Application States</strong>
          <i
            class="fa-solid fa-circle-info text-muted ms-2"
            placement="top"
            [ngbTooltip]="getModeDescription(currentMode)"></i>
        </label>
        <small class="text-muted d-block mb-2">
          {{
            currentMode === BlockStateMode.BLOCK_LISTED
              ? 'Select states that will prevent users from applying'
              : 'Select states that will allow users to apply (users must have ONLY these states)'
          }}
        </small>

        <!-- Input for adding new states -->
        <div class="input-group mb-3">
          <input
            type="text"
            class="form-control"
            placeholder="Enter application state (e.g., PENDING_RESUBMISSION)"
            [(ngModel)]="newStateInput"
            [ngModelOptions]="{ standalone: true }"
            [disabled]="!isRestrictionsEnabled"
            (keyup.enter)="addBlockedState()"
            style="text-transform: uppercase" />
          <button
            class="btn btn-primary"
            type="button"
            [disabled]="!isRestrictionsEnabled || !newStateInput.trim()"
            (click)="addBlockedState()">
            <i class="fa-solid fa-plus me-1"></i>
            Add State
          </button>
        </div>

        <!-- Display added states as chips/badges -->
        <div class="mb-3" *ngIf="blockedStates.length > 0">
          <label class="form-label text-muted small">Added States:</label>
          <div class="d-flex flex-wrap gap-2">
            <span
              *ngFor="let state of blockedStates"
              class="badge d-inline-flex align-items-center"
              [class.bg-danger]="currentMode === BlockStateMode.BLOCK_LISTED"
              [class.bg-success]="currentMode === BlockStateMode.ALLOW_LISTED"
              style="font-size: 0.9rem; padding: 0.5rem 0.75rem">
              {{ state }}
              <button
                type="button"
                class="btn-close btn-close-white ms-2"
                aria-label="Remove"
                [disabled]="!isRestrictionsEnabled"
                (click)="removeBlockedState(state)"
                style="font-size: 0.6rem"></button>
            </span>
          </div>
        </div>

        <!-- Suggested common states -->
        <div class="mt-4">
          <label class="form-label fw-bold text-dark small"
            >Common Application States (click to add):</label
          >
          <div class="d-flex flex-wrap gap-2">
            <button
              *ngFor="let state of commonApplicationStates"
              type="button"
              class="btn btn-sm suggestion-btn"
              [class.suggestion-btn-inactive]="!isStateAdded(state)"
              [class.suggestion-btn-active]="isStateAdded(state)"
              [disabled]="!isRestrictionsEnabled || isStateAdded(state)"
              (click)="addSuggestedState(state)">
              <i class="fa-solid fa-check me-1" *ngIf="isStateAdded(state)"></i>
              {{ state }}
            </button>
          </div>
        </div>

        <!-- Helper text based on mode -->
        <div
          class="alert alert-info mt-3"
          *ngIf="
            isRestrictionsEnabled && currentMode === BlockStateMode.BLOCK_LISTED
          ">
          <i class="fa-solid fa-info-circle me-2"></i>
          <strong>Block Listed Mode:</strong> When a user tries to create a new
          application for this service, the system will check if they have any
          existing applications in the states listed above. If they do, they
          will be shown an error message and will not be able to proceed.
        </div>

        <div
          class="alert alert-info mt-3"
          *ngIf="
            isRestrictionsEnabled && currentMode === BlockStateMode.ALLOW_LISTED
          ">
          <i class="fa-solid fa-info-circle me-2"></i>
          <strong>Allow Listed Mode:</strong> When a user tries to create a new
          application for this service, the system will check if ALL their
          existing applications are in the states listed above. If they have any
          applications in other states, they will be shown an error message and
          will not be able to proceed.
        </div>

        <!-- Warning when no states added but enabled -->
        <div
          class="alert alert-warning mt-3"
          *ngIf="isRestrictionsEnabled && blockedStates.length === 0">
          <i class="fa-solid fa-exclamation-triangle me-2"></i>
          <strong>Warning:</strong> The restriction feature is enabled but no
          states have been configured. Please add at least one application
          state, or disable this feature.
        </div>
      </div>
    </form>
  </div>
</div>
